import React, { Suspense, createContext, lazy, useContext, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onthoudPagina } from '../../utils/vorigePagina.ts';
import LegalPages from '../screens/LegalPages.tsx';
import { PageSkeleton, DashboardSkeleton } from '../shared/Skeletons.tsx';
import type { Person, Relationship, Source, Project, PresenceUser } from '../../types.ts';
import type { OpenEditorOptions } from '../../stores/useEditorStore.ts';

// Lazy-loaded route components
const Dashboard = lazy(() => import('../screens/Dashboard.tsx'));
const FamilyTreePage = lazy(() => import('../tree/FamilyTreePage.tsx'));
const ReportsPage = lazy(() => import('../screens/ReportsPage.tsx'));
const SearchPage = lazy(() => import('../screens/SearchPage.tsx'));
const MediaManager = lazy(() => import('../screens/MediaManager.tsx'));
const StoriesManager = lazy(() => import('../screens/StoriesManager.tsx'));
const GedcomImport = lazy(() => import('../screens/GedcomImport.tsx'));
const SourceManager = lazy(() => import('../screens/SourceManager.tsx'));
const PlaceManager = lazy(() => import('../screens/PlaceManager.tsx'));
const PersonManager = lazy(() => import('../screens/PersonManager.tsx'));
const AccountScreen = lazy(() => import('../screens/AccountScreen.tsx'));
const AuditLogScreen = lazy(() => import('../screens/AuditLogScreen.tsx'));
const DataQualityChecker = lazy(() => import('../shared/DataQualityChecker.tsx'));
const FamilyGroupSheet = lazy(() => import('../shared/FamilyGroupSheet.tsx'));
const RelationshipCalculator = lazy(() => import('../shared/RelationshipCalculator.tsx'));
const RoadmapPage = lazy(() => import('../screens/RoadmapPage.tsx'));
const PlannerPage = lazy(() => import('../planner/PlannerPage.tsx'));
const FamilyTimeline = lazy(() => import('../shared/FamilyTimeline.tsx'));

const DeletedItems = lazy(() => import('../shared/DeletedItems.tsx'));
const AnalysePage = lazy(() => import('../screens/AnalysePage.tsx'));
const SupportPage = lazy(() => import('../screens/SupportPage.tsx'));
const InvitationAcceptPage = lazy(() => import('../screens/InvitationAcceptPage.tsx'));
const AcceptInvitePage = lazy(() => import('../screens/AcceptInvitePage.tsx'));
const WelkomBijOrgPage = lazy(() => import('../screens/WelkomBijOrgPage.tsx'));
const OrganisatieScreen = lazy(() => import('../screens/OrganisatieScreen.tsx'));
const OrganisatieWizard = lazy(() => import('../screens/OrganisatieWizard.tsx'));
const BureauScreen = lazy(() => import('../screens/BureauScreen.tsx'));
const BureauKlaarScreen = lazy(() =>
  import('../screens/BureauScreen.tsx').then((m) => ({ default: m.BureauKlaarScreen }))
);
const FamilieScherm = lazy(() => import('../telefoon/FamilieScherm.tsx'));
const FamilieToevoegenScherm = lazy(() => import('../telefoon/FamilieToevoegenScherm.tsx'));
const FamilieKoppelScherm = lazy(() => import('../telefoon/FamilieKoppelScherm.tsx'));
const StyleguideScreen = lazy(() => import('../styleguide/StyleguideScreen.tsx'));

interface AppRoutesProps {
  people: Person[];
  relationships: Relationship[];
  sources: Source[];
  currentProject: Project | null;
  projects: Project[];
  rootId: string | undefined;
  treeFocusId: string | undefined;
  isViewer: boolean;
  reviewCount: number;
  user: { id: string; email?: string } | null;
  personStoryCount: Map<string, number>;
  lang: string;
  dashWidgetsUnlocked: boolean;
  setDashWidgetsUnlocked: (v: boolean) => void;
  dashShowCatalog: boolean;
  setDashShowCatalog: (v: boolean) => void;
  // Callbacks
  onSelectProject: (p: Project) => void;
  onAddPerson: () => void;
  onTreeNodeClick: (p: Person) => void;
  onSelectPerson: (id: string | null) => void;
  onPersonSaved: () => Promise<void>;
  onOpenEditor: (p: Person | null, genderOrOptions?: 'M' | 'F' | 'U' | OpenEditorOptions) => void;
  onSetProband: (personId: string, personName?: string) => void;
  onDeleteRelationship: (relId: string) => Promise<void>;
  onDeletePerson: (personId: string) => Promise<void>;
  onBulkDeletePeople: (personIds: string[]) => void;
  onStoriesChanged: () => void;
  onImportSuccess: (projectId: string, rootId?: string) => void;
  onRestoreDeleted: () => Promise<void>;
  onQualityNavigateToPerson: (id: string) => void;
  onRefreshData: () => Promise<void>;
  onNavigateToPersonInTree: (id: string) => void;
  broadcastChange: (type: string, data?: any) => void;
  wsOnDataChanged: (cb: (event: any) => void) => () => void;
}

/**
 * #1839 — presence gaat via context, niet als prop: elke presence-wijziging (ook de echo
 * van je eigen "bewerkt persoon X") liet AppRoutes en daarmee <Routes> opnieuw renderen.
 * <Routes> geeft dan een nieuwe RouteContext door, en elk scherm met useNavigate/
 * useSearchParams rendert mee — memo of niet. Alleen de stamboom leest presence.
 */
export const PresenceContext = createContext<(PresenceUser & { isMe: boolean })[]>([]);

const FamilyTreeMetPresence: React.FC<
  Omit<React.ComponentProps<typeof FamilyTreePage>, 'wsPresence'>
> = (props) => <FamilyTreePage {...props} wsPresence={useContext(PresenceContext)} />;

const AppRoutes: React.FC<AppRoutesProps> = ({
  people,
  relationships,
  sources,
  currentProject,
  projects,
  rootId,
  treeFocusId,
  isViewer,
  reviewCount,
  user,
  personStoryCount,
  lang,
  dashWidgetsUnlocked,
  setDashWidgetsUnlocked,
  dashShowCatalog,
  setDashShowCatalog,
  onSelectProject,
  onAddPerson,
  onTreeNodeClick,
  onSelectPerson,
  onPersonSaved,
  onOpenEditor,
  onSetProband,
  onDeleteRelationship,
  onDeletePerson,
  onBulkDeletePeople,
  onStoriesChanged,
  onImportSuccess,
  onRestoreDeleted,
  onQualityNavigateToPerson,
  onRefreshData,
  onNavigateToPersonInTree,
  broadcastChange,
  wsOnDataChanged,
}) => {
  // #1777 — de supportpagina stuurt mee waar de gebruiker vandaan kwam.
  const { pathname } = useLocation();
  useEffect(() => onthoudPagina(pathname), [pathname]);

  return (
    <div className="flex-1 flex overflow-hidden">
      <div className="flex-1 overflow-hidden">
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route
              path="/"
              element={
                <Suspense fallback={<DashboardSkeleton />}>
                  <Dashboard
                    people={people}
                    sources={sources}
                    project={currentProject}
                    projects={projects}
                    onSelectProject={onSelectProject}
                    onAddPerson={onAddPerson}
                    onOpenEditor={onOpenEditor}
                    reviewCount={reviewCount}
                    currentUserId={user?.id}
                    currentUser={user}
                    widgetsUnlocked={dashWidgetsUnlocked}
                    setWidgetsUnlocked={setDashWidgetsUnlocked}
                    showCatalog={dashShowCatalog}
                    setShowCatalog={setDashShowCatalog}
                  />
                </Suspense>
              }
            />
            <Route
              path="/tree"
              element={
                <FamilyTreeMetPresence
                  people={people}
                  relationships={relationships}
                  sources={sources}
                  rootId={rootId || ''}
                  // B-58 — de ECHT gekozen proband, los van de render-root. Is
                  // die leeg, dan hoort niemand het label PROBAND te krijgen.
                  probandId={currentProject?.proband_id || ''}
                  focusId={treeFocusId}
                  projectId={currentProject?.id || ''}
                  personStoryCount={personStoryCount}
                  isViewer={isViewer}
                  onNodeClick={onTreeNodeClick}
                  onSelectPerson={onSelectPerson}
                  onPersonSaved={onPersonSaved}
                  onOpenEditor={(p, ctx) => {
                    // ctx kan zowel de legacy relationship-context zijn als het
                    // nieuwe options-object met initialTab/initialSelectedEventId.
                    if (ctx && ('initialTab' in ctx || 'initialSelectedEventId' in ctx)) {
                      onOpenEditor(p, ctx);
                    } else {
                      onOpenEditor(p, (ctx as { gender?: 'M' | 'F' | 'U' } | undefined)?.gender);
                    }
                  }}
                  onSetProband={onSetProband}
                  onDeleteRelationship={onDeleteRelationship}
                  onDeletePerson={onDeletePerson}
                  wsBroadcastChange={broadcastChange}
                  wsOnDataChanged={wsOnDataChanged}
                />
              }
            />
            <Route path="/familie/:personId" element={<FamilieScherm />} />
            <Route
              path="/familie/:personId/toevoegen/:rol"
              element={
                <FamilieToevoegenScherm
                  onPersonSaved={onPersonSaved}
                  broadcastChange={broadcastChange}
                />
              }
            />
            <Route
              path="/familie/:personId/koppelen/:rol"
              element={
                <FamilieKoppelScherm
                  onPersonSaved={onPersonSaved}
                  broadcastChange={broadcastChange}
                />
              }
            />
            <Route
              path="/people"
              element={
                <PersonManager
                  projectId={currentProject?.id}
                  people={people}
                  relationships={relationships}
                  sources={sources}
                  // #1839 — stabiele referentie, anders verslaat elke App-render de memo van PersonManager.
                  onEditPerson={onOpenEditor}
                  onAddPerson={isViewer ? undefined : onAddPerson}
                  onDeletePerson={onDeletePerson}
                  onBulkDeletePeople={onBulkDeletePeople}
                  probandId={rootId}
                  isViewer={isViewer}
                  onPersonUpdated={onRefreshData}
                />
              }
            />
            <Route
              path="/media"
              element={
                <MediaManager projectId={currentProject?.id} people={people} isViewer={isViewer} />
              }
            />
            <Route
              path="/stories"
              element={
                <StoriesManager
                  projectId={currentProject?.id}
                  people={people}
                  isViewer={isViewer}
                  onStoriesChanged={onStoriesChanged}
                  onDataChanged={wsOnDataChanged}
                />
              }
            />
            <Route path="/invite" element={<InvitationAcceptPage />} />
            {/* Plan 4 (#778): publieke org-invite-flow + welkom — werkt ook voor
                ingelogde gebruikers (e-mail-binding wordt server-side gechecked). */}
            <Route path="/invite/:token" element={<AcceptInvitePage />} />
            <Route path="/welkom-bij-organisatie" element={<WelkomBijOrgPage />} />
            <Route
              path="/import"
              element={
                <GedcomImport onImportSuccess={onImportSuccess} projectId={currentProject?.id} />
              }
            />
            <Route path="/account" element={<AccountScreen />} />
            {/* Plan 3 #778 — placeholders worden in Phase D + F vervangen. */}
            <Route path="/organisatie" element={<OrganisatieScreen />} />
            <Route path="/organisatie/nieuw" element={<OrganisatieWizard />} />
            <Route path="/bureau" element={<BureauScreen />} />
            <Route path="/bureau/klaar" element={<BureauKlaarScreen />} />
            {/* Brand guide / styleguide (#890 Sprint 1) — route is open;
                sidebar-link is role-conditional in AppSidebar. */}
            <Route path="/styleguide" element={<StyleguideScreen />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/privacy" element={<LegalPages page="privacy" lang={lang} />} />
            <Route path="/terms" element={<LegalPages page="terms" lang={lang} />} />
            <Route
              path="/sources"
              element={
                <SourceManager
                  projectId={currentProject?.id}
                  people={people}
                  isViewer={isViewer}
                  wsOnDataChanged={wsOnDataChanged}
                />
              }
            />
            <Route
              path="/places"
              element={
                <PlaceManager
                  projectId={currentProject?.id}
                  // #1533 — zelfde proband als Statistieken, voor de bloedlijn-schakelaar.
                  probandId={currentProject?.proband_id ?? rootId}
                  isViewer={isViewer}
                  wsOnDataChanged={wsOnDataChanged}
                />
              }
            />
            <Route
              path="/reports"
              element={
                <ReportsPage
                  people={people}
                  relationships={relationships}
                  sources={sources}
                  projectId={currentProject?.id}
                  onRefreshData={onRefreshData}
                />
              }
            />
            <Route path="/search" element={<SearchPage people={people} />} />
            <Route path="/audit" element={<AuditLogScreen projectId={currentProject?.id} />} />
            <Route
              path="/planner"
              element={
                <PlannerPage
                  projectId={currentProject?.id || ''}
                  people={people}
                  sources={sources}
                  currentUserId={user?.id}
                  isViewer={isViewer}
                />
              }
            />
            <Route
              path="/timeline"
              element={
                <FamilyTimeline
                  people={people}
                  relationships={relationships}
                  projectId={currentProject?.id}
                />
              }
            />
            {/* /occupations, /name-statistics, /calendar removed — now in /analyse */}
            <Route
              path="/analyse"
              element={
                <AnalysePage
                  people={people}
                  relationships={relationships}
                  sources={sources}
                  projectId={currentProject?.id}
                  // B-33 — de generatie-verdeling las de opgeslagen kolom
                  // `sosa_number`, en die is in de praktijk leeg (één gevulde
                  // waarde per project: de proband). Met de proband erbij kan
                  // Demografie de generaties berekenen, net als het dashboard.
                  probandId={currentProject?.proband_id ?? rootId}
                />
              }
            />
            <Route path="/roadmap" element={<RoadmapPage />} />
            <Route
              path="/family-sheet"
              element={
                <FamilyGroupSheet
                  people={people}
                  relationships={relationships}
                  sources={sources}
                  onNavigateToPerson={onNavigateToPersonInTree}
                  projectId={currentProject?.id}
                />
              }
            />
            <Route
              path="/relationships"
              element={
                <RelationshipCalculator
                  people={people}
                  relationships={relationships}
                  onNavigateToPerson={onNavigateToPersonInTree}
                />
              }
            />
            <Route
              path="/quality"
              element={
                <DataQualityChecker
                  projectId={currentProject?.id || ''}
                  people={people}
                  relationships={relationships}
                  sources={sources}
                  onNavigateToPerson={onQualityNavigateToPerson}
                  onDataChanged={onRefreshData}
                />
              }
            />
            <Route
              path="/deleted"
              element={
                <DeletedItems projectId={currentProject?.id || ''} onRestore={onRestoreDeleted} />
              }
            />
            {/* Catch-all fallback — prevents blank page on broken links (Refs #614) */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
};

// #1839 — memo: App rendert om veel redenen (sidebar, presence, wachtrij, …); de routes
// alleen als hun eigen invoer verandert. Vereist stabiele callbacks vanuit App.
export default React.memo(AppRoutes);
